import React from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, Star, ExternalLink, SearchX } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import { pickOutfitImage, APPAREL_IMAGES } from "../../../data/looks";
import StepFrame, { StepTitle } from "../StepFrame";

export default function OutfitOptionsStep() {
  const {
    outfitOptions, loadingOutfits, loadOutfits, loadSampleOutfits, myntraStatus, isSampleOutfits,
    selectedOutfit, setSelectedOutfit, styleDirection, loadAccessoriesProducts, goTo,
  } = useHOH();

  const proceed = () => { loadAccessoriesProducts(); goTo(STEP.ACCESSORIES); };
  const empty = !loadingOutfits && myntraStatus === "no_products" && (!outfitOptions || outfitOptions.length === 0);

  return (
    <StepFrame wide className="text-center">
      <StepTitle
        overline="Step 4"
        title="Choose your outfit"
        subtitle={isSampleOutfits ? "Sample looks (add a live data key for real Myntra products)." : "Real Myntra picks curated for your occasion."}
      />

      {loadingOutfits || outfitOptions === null ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="hoh-card p-4"><div className="aspect-[4/5] rounded-[18px] bg-hoh-lavender flex items-center justify-center"><Loader2 className="w-7 h-7 text-hoh-rose animate-spin" /></div></div>
          ))}
        </div>
      ) : empty ? (
        <div className="hoh-card p-10 md:p-14 max-w-lg mx-auto">
          <div className="w-16 h-16 mx-auto rounded-full bg-hoh-rose/15 flex items-center justify-center"><SearchX className="w-8 h-8 text-hoh-rose" /></div>
          <h3 className="mt-5 text-2xl font-semibold text-black">We couldn’t find an exact match.</h3>
          <p className="mt-3 text-hoh-slate">Try again with broader keywords, or preview the experience with sample looks.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={loadOutfits} className="btn-ghost" data-testid="retry-outfits-btn">Retry Search</button>
            <button onClick={loadSampleOutfits} className="btn-royal" data-testid="sample-outfits-btn">Preview With Samples</button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {outfitOptions.map((o, i) => {
              const selected = selectedOutfit?.title === o.title;
              const img = o.image || pickOutfitImage(i, styleDirection);
              return (
                <motion.div key={(o.title || "") + i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 3) * 0.08 }}
                  className={`hoh-card p-4 flex flex-col text-left ${selected ? "hoh-glow-border" : ""}`} data-testid={`outfit-option-${i}`}>
                  <div className="aspect-[4/5] rounded-[18px] overflow-hidden bg-hoh-cream">
                    <img src={img} alt={o.title} onError={(e) => { e.currentTarget.src = APPAREL_IMAGES.top; }} className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-4 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-wider text-hoh-rose font-semibold">{o.brand}</span>
                      {o.rating && <span className="flex items-center gap-1 text-xs text-hoh-slate"><Star className="w-3 h-3 fill-hoh-rose text-hoh-rose" />{o.rating}</span>}
                    </div>
                    <h3 className="mt-1 text-sm font-semibold text-black leading-snug line-clamp-2">{o.title}</h3>
                    <p className="mt-2 text-lg font-semibold text-black">{o.price || "—"}</p>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => setSelectedOutfit(o)} className={`flex-1 rounded-full px-4 py-3 font-semibold uppercase tracking-wider text-xs transition-colors ${selected ? "bg-hoh-rose text-hoh-navy" : "bg-hoh-navy text-white hover:bg-[#2D3A54]"}`} data-testid={`outfit-select-${i}`}>
                      {selected ? <span className="flex items-center justify-center gap-1.5"><Check className="w-4 h-4" /> Selected</span> : "Select Outfit"}
                    </button>
                    {o.url && (
                      <a href={o.url} target="_blank" rel="noreferrer" className="rounded-full px-3 py-3 border border-hoh-navy/20 text-hoh-navy hover:bg-hoh-blush flex items-center justify-center" data-testid={`outfit-view-${i}`} title="View on Myntra">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10 min-h-[60px]">
            {selectedOutfit && (
              <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onClick={proceed} className="btn-royal" data-testid="outfit-continue-btn">
                Continue To Accessories <ArrowRight className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </>
      )}
    </StepFrame>
  );
}
